import React, { useState } from "react";
import {
  Building2,
  Building,
  ShoppingBag,
  Landmark,
  Hospital,
  Home,
  Briefcase,
  GraduationCap,
  Phone,
  Mail,
  MapPin,
  Download,
  CheckCircle,
  Shield,
  Users,
  Award,
  Clock,
  Star,
  Send,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Color constants
const PAPAYA_ORANGE = "#FF7F00";
const PAPAYA_DARK = "#E56B00";
const CHARCOAL = "#2D2D2D";
const DARK_GRAY = "#3D3D3D";

const CapabilityStatement = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleDownloadPDF = () => {
    window.open(`${BACKEND_URL}/api/download/capability-statement`, '_blank');
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to a backend
    console.log('Form submitted:', formData);
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 3000);
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const services = [
    {
      icon: Building2,
      title: "Commercial Spaces",
      description: "Professional cleaning for offices and commercial spaces.",
      features: ["Professional cleaners", "High hygiene standards", "Quality-checked"],
    },
    {
      icon: Building,
      title: "High-Rise Buildings",
      description: "Safe, dependable high-rise and window cleaning.",
      features: ["Safety-first", "Windows & facades", "Reliable"],
    },
    {
      icon: ShoppingBag,
      title: "Retail Spaces",
      description: "Cleaning solutions for retail environments.",
      features: ["Customer-area focused", "After-hours service", "Spotless results"],
    },
    {
      icon: Landmark,
      title: "Government Facilities",
      description: "Secure cleaning for government facilities.",
      features: ["Vetted staff", "Punctual service", "Professional"],
    },
    {
      icon: Hospital,
      title: "Hospitals & Medical",
      description: "Specialised healthcare cleaning services.",
      features: ["Infection control", "High hygiene", "Safe products"],
    },
    {
      icon: Home,
      title: "Strata Management",
      description: "Expert cleaning for strata properties.",
      features: ["Common areas", "Scheduled cleaning", "Property care"],
    },
    {
      icon: Briefcase,
      title: "Private Contractors",
      description: "Flexible cleaning for contractors.",
      features: ["Site-ready", "Flexible scheduling", "Safety-focused"],
    },
    {
      icon: GraduationCap,
      title: "Schools & Universities",
      description: "Healthy environments for education.",
      features: ["Safe cleaning", "Hygiene focus", "Flexible schedules"],
    },
  ];

  const testimonials = [
    {
      name: "Anna Makry",
      role: "Client",
      text: "Sam at Rex Cleaning provided top-quality cleaning services for our clients. Their team was efficient, dependable, and thorough throughout each project.",
    },
    {
      name: "Nasser Mousa",
      role: "Manager",
      text: "Working with REX Cleaning has been an excellent experience. Brian and his team were professional, reliable, and genuinely committed to delivering spotless results.",
    },
    {
      name: "Josh Eid",
      role: "Manager",
      text: "Rex Cleaning did an excellent job from start to finish. Sam was really professional, on time, and very detail-oriented. The space was left spotless and fresh.",
    },
    {
      name: "Lee Trad",
      role: "Manager",
      text: "Rex Cleaning exceeded my expectations with their attention to detail. Shareef was punctual, professional, and efficient. Everything was left sparkling clean.",
    },
    {
      name: "Mathew T",
      role: "Client",
      text: "Rex Cleaning consistently delivered excellent service. Brian and his team showed professionalism, reliability, and strong attention to detail on every job.",
    },
    {
      name: "Dilar Singh",
      role: "Client",
      text: "We are very grateful to Shareef for the outstanding work done for our clients. They communicated clearly, arrived on time, and maintained a high standard.",
    },
  ];

  const competencies = [
    { icon: Shield, title: "WHS Compliant", description: "Full workplace health & safety compliance" },
    { icon: Users, title: "Vetted Staff", description: "Background-checked & trained professionals" },
    { icon: Award, title: "Quality Assured", description: "Consistent high-standard service delivery" },
    { icon: Clock, title: "Flexible Scheduling", description: "24/7 availability including weekends" },
  ];

  const keyClients = [
    {
      name: "HealthShare NSW",
      logo: "https://customer-assets.emergentagent.com/job_capability-builder-3/artifacts/awbd5l23_healthsharensw.png",
    },
    {
      name: "Montessori Academy",
      logo: "https://customer-assets.emergentagent.com/job_capability-builder-3/artifacts/wlnilxty_montessori.webp",
    },
    {
      name: "Empowered Early Learning",
      logo: "https://customer-assets.emergentagent.com/job_capability-builder-3/artifacts/8qus5u8c_images.png",
    },
    {
      name: "Provelop",
      logo: "https://customer-assets.emergentagent.com/job_capability-builder-3/artifacts/pyo8g06u_download.jpeg",
    },
    {
      name: "CPG",
      logo: "https://customer-assets.emergentagent.com/job_capability-builder-3/artifacts/xpqvu2nw_image-50-300x264.png",
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Inter']" data-testid="capability-statement">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 print:static print:border-none">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="company-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_capability-builder-3/artifacts/msarje01_WhatsApp%20Image%202026-01-31%20at%2023.49.57.jpeg"
              alt="REX Cleaning Logo"
              className="h-14 w-auto"
            />
          </div>
          <div className="hidden md:flex items-center gap-4 print:hidden">
            <a href="#services" className="text-sm text-gray-600 hover:text-gray-900 transition-colors" style={{ '--hover-color': PAPAYA_ORANGE }} data-testid="nav-services">Services</a>
            <a href="#about" className="text-sm text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-about">About</a>
            <a href="#clients" className="text-sm text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-clients">Clients</a>
            <a href="#team" className="text-sm text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-team">Team</a>
            <a href="#contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors" data-testid="nav-contact">Contact</a>
          </div>
          <div className="flex items-center gap-3 print:hidden">
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
              data-testid="download-pdf-button"
              aria-label="Download PDF"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              PDF
            </button>
            <a
              href="tel:0294241284"
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-md transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
              style={{ backgroundColor: PAPAYA_ORANGE }}
              onMouseEnter={(e) => e.target.style.backgroundColor = PAPAYA_DARK}
              onMouseLeave={(e) => e.target.style.backgroundColor = PAPAYA_ORANGE}
              data-testid="contact-button"
              aria-label="Contact us"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              02 9424 1284
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?w=1920&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-['Manrope'] tracking-tight mb-6" data-testid="hero-title">
                Premium Commercial Cleaning for Government & Enterprise
              </h1>
              <p className="text-lg text-gray-200 leading-relaxed mb-6">
                Trusted, compliant cleaning solutions across NSW for government, healthcare, corporate, and educational facilities.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:0294241284"
                  className="flex items-center gap-2 px-6 py-3 text-base font-semibold bg-white rounded-md hover:bg-gray-100 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95 print:hidden"
                  style={{ color: CHARCOAL }}
                  data-testid="call-button"
                >
                  <Phone className="w-5 h-5" strokeWidth={1.5} />
                  02 9424 1284
                </a>
                <button
                  onClick={handleDownloadPDF}
                  className="flex items-center gap-2 px-6 py-3 text-base font-semibold text-white border-2 border-white rounded-md hover:bg-white/10 transition-all duration-200 print:hidden"
                  data-testid="download-button"
                >
                  <Download className="w-5 h-5" strokeWidth={1.5} />
                  Download PDF
                </button>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl" data-testid="contact-form">
              <h3 className="text-xl font-bold mb-1 font-['Manrope']" style={{ color: CHARCOAL }}>Get a Free Quote</h3>
              <p className="text-gray-500 text-sm mb-6">We'll get back to you within 24 hours</p>
              
              {formSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 127, 0, 0.1)' }}>
                    <CheckCircle className="w-8 h-8" style={{ color: PAPAYA_ORANGE }} />
                  </div>
                  <p className="font-semibold" style={{ color: CHARCOAL }}>Thank you!</p>
                  <p className="text-gray-500 text-sm">We'll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Your Name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all text-sm"
                      data-testid="form-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="email"
                      placeholder="Email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all text-sm"
                      data-testid="form-email"
                    />
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all text-sm"
                      data-testid="form-phone"
                    />
                  </div>
                  <div>
                    <textarea
                      placeholder="Tell us about your cleaning needs..."
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition-all text-sm resize-none"
                      data-testid="form-message"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                    style={{ backgroundColor: PAPAYA_ORANGE }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = PAPAYA_DARK}
                    onMouseLeave={(e) => e.target.style.backgroundColor = PAPAYA_ORANGE}
                    data-testid="form-submit"
                  >
                    <Send className="w-5 h-5" strokeWidth={1.5} />
                    Request Quote
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section id="about" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium uppercase tracking-wider mb-4 block" style={{ color: PAPAYA_ORANGE }}>About Us</span>
              <h2 className="text-3xl md:text-4xl font-semibold font-['Manrope'] tracking-tight mb-6" style={{ color: CHARCOAL }} data-testid="about-title">
                Trusted Cleaning Partner
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                REX Cleaning delivers high-quality commercial cleaning across NSW. We serve offices, retail, hospitals, government facilities, high-rise buildings, strata properties, and schools with reliability and attention to detail.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {competencies.map((comp, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255, 127, 0, 0.1)' }}>
                      <comp.icon className="w-5 h-5" style={{ color: PAPAYA_ORANGE }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm" style={{ color: CHARCOAL }}>{comp.title}</h4>
                      <p className="text-xs text-gray-500">{comp.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=600&fit=crop"
                  alt="Professional cleaning team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg border border-gray-100 print:static print:shadow-none">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: PAPAYA_ORANGE }}>
                    <CheckCircle className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-['Manrope']" style={{ color: CHARCOAL }}>100%</p>
                    <p className="text-sm text-gray-500">Client Satisfaction</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Competencies - Even Grid */}
      <section id="services" className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-sm font-medium uppercase tracking-wider mb-4 block" style={{ color: PAPAYA_ORANGE }}>Our Services</span>
            <h2 className="text-3xl md:text-4xl font-semibold font-['Manrope'] tracking-tight mb-4" style={{ color: CHARCOAL }} data-testid="services-title">
              Industry-Specific Cleaning
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-100 rounded-xl p-5 hover:shadow-lg transition-all duration-300"
                data-testid={`service-card-${idx}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: 'rgba(255, 127, 0, 0.1)' }}>
                  <service.icon className="w-5 h-5" style={{ color: PAPAYA_ORANGE }} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold font-['Manrope'] mb-1" style={{ color: CHARCOAL }}>{service.title}</h3>
                <p className="text-gray-500 text-xs">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Clients Section */}
      <section id="clients" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-sm font-medium uppercase tracking-wider mb-4 block" style={{ color: PAPAYA_ORANGE }}>Trusted By</span>
            <h2 className="text-3xl md:text-4xl font-semibold font-['Manrope'] tracking-tight mb-4" style={{ color: CHARCOAL }} data-testid="clients-title">
              Key Clients
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {keyClients.map((client, idx) => (
              <div
                key={idx}
                className="flex items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300"
                data-testid={`client-logo-${idx}`}
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="h-16 md:h-20 w-auto max-w-[160px] object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Performance / Testimonials */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-sm font-medium uppercase tracking-wider mb-4 block" style={{ color: PAPAYA_ORANGE }}>Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-semibold font-['Manrope'] tracking-tight mb-4" style={{ color: CHARCOAL }} data-testid="testimonials-title">
              What Our Clients Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-md transition-all duration-300"
                data-testid={`testimonial-card-${idx}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" strokeWidth={1.5} />
                  ))}
                </div>
                <p className="text-gray-600 text-sm mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 127, 0, 0.15)' }}>
                    <span className="font-semibold text-sm" style={{ color: PAPAYA_ORANGE }}>{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-sm" style={{ color: CHARCOAL }}>{testimonial.name}</p>
                    <p className="text-xs text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Personnel */}
      <section id="team" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-sm font-medium uppercase tracking-wider mb-4 block" style={{ color: PAPAYA_ORANGE }}>Leadership</span>
            <h2 className="text-3xl md:text-4xl font-semibold font-['Manrope'] tracking-tight mb-4" style={{ color: CHARCOAL }} data-testid="team-title">
              Key Personnel
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              {
                name: "Shareef Hassan",
                role: "Director",
              },
              {
                name: "Hasan Elarayfi",
                role: "Director",
              },
              {
                name: "Brian",
                role: "Operational Manager",
              },
              {
                name: "Ayah Kadmane",
                role: "Supervisor",
              },
            ].map((person, idx) => (
              <div
                key={idx}
                className="bg-gray-50 border border-gray-100 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300"
                data-testid={`personnel-card-${idx}`}
              >
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${PAPAYA_ORANGE}, ${PAPAYA_DARK})` }}>
                  <span className="text-xl font-bold text-white font-['Manrope']">{person.name.charAt(0)}</span>
                </div>
                <h3 className="text-base font-semibold font-['Manrope'] mb-1" style={{ color: CHARCOAL }}>{person.name}</h3>
                <p className="font-medium text-xs" style={{ color: PAPAYA_ORANGE }}>{person.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Compliance */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-sm font-medium uppercase tracking-wider mb-4 block" style={{ color: PAPAYA_ORANGE }}>Compliance</span>
            <h2 className="text-3xl md:text-4xl font-semibold font-['Manrope'] tracking-tight mb-4" style={{ color: CHARCOAL }} data-testid="compliance-title">
              Certifications
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: "ABN Registered", value: "95 693 844 227", icon: Briefcase },
              { title: "WHS Compliant", value: "Workplace Health & Safety", icon: Shield },
              { title: "Fully Insured", value: "Public Liability", icon: CheckCircle },
              { title: "Background Checked", value: "Vetted Staff", icon: Users },
            ].map((cert, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-100 rounded-xl p-5 text-center"
                data-testid={`certification-${idx}`}
              >
                <div className="w-10 h-10 rounded-lg mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 127, 0, 0.1)' }}>
                  <cert.icon className="w-5 h-5" style={{ color: PAPAYA_ORANGE }} strokeWidth={1.5} />
                </div>
                <h4 className="font-semibold text-sm mb-1" style={{ color: CHARCOAL }}>{cert.title}</h4>
                <p className="text-xs text-gray-500">{cert.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Footer */}
      <footer id="contact" className="py-16 md:py-20" style={{ backgroundColor: CHARCOAL }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_capability-builder-3/artifacts/msarje01_WhatsApp%20Image%202026-01-31%20at%2023.49.57.jpeg"
                  alt="REX Cleaning Logo"
                  className="h-14 w-auto"
                />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-md">
                REX Cleaning is dedicated to maintaining environments that people can trust, work in, learn in, and recover in — with confidence.
              </p>
              <p className="text-gray-500 text-xs">
                ABN: 95 693 844 227 | ACN: 693 844 227
              </p>
            </div>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h4 className="text-white font-semibold mb-4">Contact Us</h4>
                <ul className="space-y-3">
                  <li>
                    <a
                      href="tel:0294241284"
                      className="flex items-center gap-2 text-gray-400 transition-colors text-sm"
                      style={{ '--hover-color': PAPAYA_ORANGE }}
                      onMouseEnter={(e) => e.target.style.color = PAPAYA_ORANGE}
                      onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                      data-testid="footer-phone"
                    >
                      <Phone className="w-4 h-4" strokeWidth={1.5} />
                      02 9424 1284
                    </a>
                  </li>
                  <li>
                    <a
                      href="mailto:info@rexcleaning.com.au"
                      className="flex items-center gap-2 text-gray-400 transition-colors text-sm"
                      onMouseEnter={(e) => e.target.style.color = PAPAYA_ORANGE}
                      onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                      data-testid="footer-email"
                    >
                      <Mail className="w-4 h-4" strokeWidth={1.5} />
                      info@rexcleaning.com.au
                    </a>
                  </li>
                  <li>
                    <span className="flex items-center gap-2 text-gray-400 text-sm">
                      <MapPin className="w-4 h-4" strokeWidth={1.5} />
                      NSW, Australia
                    </span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Services</h4>
                <ul className="space-y-2">
                  <li><span className="text-gray-400 text-sm">Commercial Spaces</span></li>
                  <li><span className="text-gray-400 text-sm">High-Rise Buildings</span></li>
                  <li><span className="text-gray-400 text-sm">Government Facilities</span></li>
                  <li><span className="text-gray-400 text-sm">Healthcare</span></li>
                  <li><span className="text-gray-400 text-sm">Education</span></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} REX Cleaning. All rights reserved.
              </p>
              <p className="text-gray-500 text-sm">
                Professional Cleaning Services NSW
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Print Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap');
        
        @media print {
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          section {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default CapabilityStatement;
